import { LicenceSetting } from '../licence';
import { RecommendSetting } from '../recommend';
import { StatsSetting } from '../stats';
import { useEffect, useState } from 'react';
import type { BeekeeperProfile, Product, TreatmentEvent, TreatmentMethod } from '../../db/model';
import { exportBackup, getSetting, importBackup, read, setSetting, changesSince, tx } from '../../db/db';
import { fmt, naturalLabel, treatmentsInYear } from '../../db/queries';
import { saveProduct } from '../../db/actions';
import { todayISO, withdrawalUntil } from '../../db/rules';
import { MONTHS, SEASON_SETTING, normStartMonth, seasonOf, seasonSettingLabel } from '../../db/season';
import { qrSvg } from '../../lib/qr';
import { go, useQuery } from '../hooks';
import { colonyName, TREATMENT_METHOD, fmtDate, changedRecords } from '../labels';
import { CustomLinesSetting } from '../queen-lines';
import { ExportButtons } from '../export';
import { QueenAgeSetting } from '../queen-age';
import { ReportReminderSetting } from '../report-reminder';
import { Chips, Empty, Field, Link, Notice, Screen, Section, Stepper, TextInput, toast } from '../ui';
import { confirmSmall, FxSetting, saved } from '../confirm';
import { TextSizeSetting, SunSetting } from '../display';
import { Icon } from '../icons';

// ---------------------------------------------------------------------------
// Evidence použitých léčiv
// ---------------------------------------------------------------------------

export function TreatmentLogScreen() {
  const [year, setYear] = useState(new Date().getFullYear());
  const data = useQuery(async () => {
    const [rows, apiaries, colonies, profile, treatYears] = await Promise.all([
      treatmentsInYear(year),
      // i smazaná (v koši) – léčení v evidenci zůstává a musí mít název včelstva a stanoviště
      tx(['apiaries'], 'readonly', (t) => t.all('apiaries')),
      tx(['colonies'], 'readonly', (t) => t.all('colonies')),
      getSetting<BeekeeperProfile>('profile'),
      // roky, ve kterých je zapsané nějaké léčení (evidence se uchovává 5 let, starší data se ale také nabídnou)
      read.byIndex('events', 'type', 'treatment').then((xs) => new Set(xs.map((e) => Number(e.date?.slice(0, 4))).filter((n) => n > 1900))),
    ]);
    return { rows, apiaries, colonies, profile, treatYears };
  }, [year]);
  if (!data) return null;
  const { rows, apiaries, colonies, profile, treatYears } = data;
  const apName = (id: string) => apiaries.find((a) => a.id === id)?.name ?? '';
  const labels = (ids: string[]) =>
    ids
      .map((id) => colonies.find((c) => c.id === id))
      .filter(Boolean)
      .sort((a, b) => naturalLabel(a!, b!))
      .map((c) => colonyName(c!.label))
      .join(', ');

  // záznamy z jednoho zápisu pro celé stanoviště spojit do jednoho řádku,
  // pokud se později neupravily (stejné datum, přípravek, šarže, dávka)
  const groups = new Map<string, TreatmentEvent>();
  for (const e of rows) {
    const key = [e.batchId ?? e.id, e.date, e.endDate, e.productId, e.batchNumber, e.dosePerColony, e.performedBy].join('|');
    const g = groups.get(key);
    if (g) g.colonyIds = [...g.colonyIds, ...e.colonyIds];
    else groups.set(key, { ...e, colonyIds: [...e.colonyIds] });
  }
  const table = [...groups.values()].map((e) => ({
    datum: fmtDate(e.date),
    konec: fmtDate(e.endDate),
    stanoviste: apName(e.apiaryId),
    vcelstva: labels(e.colonyIds),
    pocet: e.colonyIds.length,
    pripravek: e.productName,
    sarze: e.batchNumber ?? '',
    davka: e.dosePerColony != null ? `${fmt(e.dosePerColony, 2)} ${e.unit ?? ''}`.trim() : '',
    zpusob: TREATMENT_METHOD[e.method],
    lhuta: withdrawalUntil(e) ? fmtDate(withdrawalUntil(e)) : e.withdrawalDays === 0 ? '0 dní' : '',
    provedl: e.performedBy ?? '',
  }));

  const exportTable = {
    name: `Evidence léčiv ${year}`,
    head: ['Datum', 'Konec', 'Stanoviště', 'Včelstva', 'Počet', 'Přípravek', 'Šarže', 'Dávka/včelstvo', 'Způsob', 'Ochranná lhůta do', 'Provedl'],
    rows: table.map((r) => Object.values(r)),
  };

  const y = new Date().getFullYear();
  // posledních 5 let (doba uchování evidence) + starší roky, pokud v nich je léčení
  const years = [...new Set([y, y - 1, y - 2, y - 3, y - 4, ...treatYears])].filter((n) => n <= y).sort((a, b) => b - a);
  return (
    <Screen title="Evidence léčiv" subtitle="Záznamy o použití veterinárních léčiv" backTo="prehledy">
      <div className="no-print">
        <Chips options={years.map((n) => [n, String(n)] as [number, string])} value={year} onChange={(v) => v && setYear(v)} />
      </div>
      <div className="print-only print-head">
        <h1>Evidence použitých veterinárních léčiv – {year}</h1>
        <p>
          Chovatel: {profile?.name ?? '……………'} · Reg. č.: {profile?.registrationNumber ?? '……………'}
        </p>
      </div>
      {table.length === 0 ? (
        <Empty>V roce {year} nejsou zapsaná žádná léčení.</Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table className="log">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Stanoviště / včelstva</th>
                  <th>Přípravek</th>
                  <th>Šarže</th>
                  <th>Dávka</th>
                  <th>Lhůta do</th>
                  <th>Provedl</th>
                </tr>
              </thead>
              <tbody>
                {table.map((r, i) => (
                  <tr key={i}>
                    <td>
                      {r.datum}
                      {r.konec && <div className="small muted">do {r.konec}</div>}
                    </td>
                    <td>
                      {r.stanoviste}
                      <div className="small muted">
                        {r.pocet}× ({r.vcelstva})
                      </div>
                    </td>
                    <td>
                      {r.pripravek}
                      <div className="small muted">{r.zpusob}</div>
                    </td>
                    <td>{r.sarze}</td>
                    <td>{r.davka}</td>
                    <td>{r.lhuta}</td>
                    <td>{r.provedl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ExportButtons table={exportTable} />
        </>
      )}
      {!profile?.registrationNumber && (
        <p className="muted small no-print">
          Doplňte své údaje chovatele v <Link to="nastaveni">Nastavení</Link>, objeví se v hlavičce tisku.
        </p>
      )}
    </Screen>
  );
}

function download(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// Nastavení: údaje chovatele, záloha, přípravky
// ---------------------------------------------------------------------------

// rozbalený seznam přípravků zůstane rozbalený i po návratu z úpravy přípravku
const PRODUCTS_OPEN = 'ulnik-products-open';
const productsOpen = () => {
  try {
    return sessionStorage.getItem(PRODUCTS_OPEN) === '1';
  } catch {
    return false;
  }
};
const rememberProductsOpen = (open: boolean) => {
  try {
    sessionStorage.setItem(PRODUCTS_OPEN, open ? '1' : '0');
  } catch {
    /* nevadí */
  }
};

/** Umí telefon sdílet soubor (iPhone, Android)? Na počítači většinou ne. */
const canShareFiles = () => {
  try {
    return !!navigator.canShare?.({ files: [new File(['{}'], 'test.json', { type: 'application/json' })] });
  } catch {
    return false;
  }
};

export function SettingsScreen() {
  const [profile, setProfile] = useState<BeekeeperProfile | null>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [pending, setPending] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const data = useQuery(async () => ({
    products: await read.all('products'),
    lastBackup: await getSetting<string>('lastBackupAt'),
    changes: await changesSince(await getSetting<string>('lastBackupAt')),
  }), []);
  // Zálohu pro sdílení připravíme předem: iPhone dovolí sdílet jen hned po ťuknutí,
  // čekání na databázi by sdílení zablokovalo. Obnoví se při každé změně dat.
  const [prepared, setPrepared] = useState<{ file: File; at: string } | null | undefined>(undefined);
  useEffect(() => {
    if (!canShareFiles()) return setPrepared(null);
    let alive = true;
    exportBackup().then((b) => {
      if (alive)
        setPrepared({ file: new File([JSON.stringify(b)], `ulnik-zaloha-${b.exportedAt.slice(0, 10)}.json`, { type: 'application/json' }), at: b.exportedAt });
    });
    return () => {
      alive = false;
    };
  }, [data]);
  useEffect(() => {
    getSetting<BeekeeperProfile>('profile').then((p) => setProfile(p ?? {}));
    navigator.storage?.persisted?.().then(setPersisted);
  }, []);
  if (!profile || !data || prepared === undefined) return <Screen title="Nastavení" backTo={false}>{null}</Screen>;

  const saveProfile = async () => {
    await setSetting('profile', profile);
    saved();
  };

  const doShare = async () => {
    if (!prepared) return doExport();
    try {
      await navigator.share({ files: [prepared.file], title: 'Záloha Úlníku', text: `Záloha aplikace Úlník z ${fmtDate(prepared.at)}` });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return; // uživatel sdílení zavřel
      return doExport();
    }
    await setSetting('lastBackupAt', prepared.at);
    saved('Záloha odeslána');
  };

  const doExport = async () => {
    const backup = await exportBackup();
    const stamp = backup.exportedAt.slice(0, 10);
    download(`ulnik-zaloha-${stamp}.json`, JSON.stringify(backup), 'application/json');
    await setSetting('lastBackupAt', backup.exportedAt);
    saved('Záloha stažena');
    setTimeout(() => toast('Zálohu uložte mimo telefon (e-mail, Disk).'), 1400);
  };

  const readImport = async (file: File) => {
    try {
      setPending(JSON.parse(await file.text()));
    } catch {
      toast('Soubor se nepodařilo načíst.');
    }
  };

  const doImport = async () => {
    if (importing) return;
    setImporting(true);
    try {
      await importBackup(pending);
      setPending(null);
      saved('Záloha obnovena');
      go('', true);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Zálohu se nepodařilo obnovit.');
    } finally {
      setImporting(false);
    }
  };

  const requestPersist = async () => {
    const ok = await navigator.storage?.persist?.();
    setPersisted(!!ok);
    toast(ok ? 'Prohlížeč data nesmaže.' : 'Prohlížeč trvalé uložení nepovolil – pravidelně zálohujte.');
  };

  return (
    <Screen title="Nastavení" backTo={false}>
      <div className="settings">
      <h2 className="settings-group">Chovatel a včelstva</h2>
      <Section title="Údaje chovatele">
        <Field label="Jméno" htmlFor="p-name">
          <TextInput id="p-name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} />
        </Field>
        <Field label="Registrační číslo chovatele" htmlFor="p-reg" hint="ČMSCH – objeví se v evidenci léčiv">
          <TextInput id="p-reg" value={profile.registrationNumber} onChange={(v) => setProfile({ ...profile, registrationNumber: v })} />
        </Field>
        <Field label="Adresa" htmlFor="p-addr">
          <TextInput id="p-addr" value={profile.address} onChange={(v) => setProfile({ ...profile, address: v })} />
        </Field>
        <button className="btn primary" onClick={saveProfile}>
          Uložit údaje
        </button>
      </Section>
      <SeasonSetting />
      <QueenAgeSetting />
      <CustomLinesSetting />
      <details
        className="section collapsible"
        open={productsOpen()}
        onToggle={(e) => rememberProductsOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary>
          <h2>Přípravky k léčení</h2>
          <span className="summary-meta">{data.products.filter((p) => !p.hidden).length} v nabídce</span>
        </summary>
        <p className="muted">Přípravky, které nabízí zápis léčení. Ťuknutím upravíte dávkování a ochrannou lhůtu nebo přípravek skryjete.</p>
        <ul className="list">
          {data.products
            .sort((a, b) => Number(a.hidden) - Number(b.hidden) || (a.name ?? '').localeCompare(b.name ?? '', 'cs'))
            .map((p) => (
              <li key={p.id}>
                <Link to={`pripravek/${p.id}`} className={'list-item' + (p.hidden ? ' inactive' : '')}>
                  <span>
                    {p.name}
                    <span className="small muted"> · {p.activeSubstance}</span>
                  </span>
                  <span className="small muted">{p.withdrawalDays != null ? `lhůta ${p.withdrawalDays} d` : 'lhůta nevyplněna'}</span>
                </Link>
              </li>
            ))}
        </ul>
        <button className="btn wide" onClick={() => go('pripravek/novy')}>
          + Přidat přípravek
        </button>
      </details>

      <h2 className="settings-group">Kalendář</h2>
      <ReportReminderSetting />

      <h2 className="settings-group">Vzhled a ovládání</h2>
      <TextSizeSetting />
      <SunSetting />
      <FxSetting />

      <h2 className="settings-group">Data</h2>
      <Section title="Záloha dat">
        <p className="muted">
          Všechna data jsou uložená jen v tomto telefonu. Zálohu si stáhněte a uložte jinam – při ztrátě telefonu je to jediná cesta, jak data
          obnovit.
        </p>
        <p className="small">
          {data.lastBackup ? `Poslední záloha: ${fmtDate(data.lastBackup)}` : 'Zatím žádná záloha.'}
          {data.lastBackup && data.changes > 0 && (
            <strong className="warn-text"> Od té doby {changedRecords(data.changes)}.</strong>
          )}
        </p>
        {canShareFiles() && (
          <>
            <button className="btn primary wide" onClick={doShare}>
              <Icon name="share" size={20} /> Poslat zálohu (e‑mail, Disk, iCloud…)
            </button>
            <p className="small muted">Nejjistější je poslat zálohu e‑mailem sám sobě – zůstane v poště, i kdyby se telefon ztratil.</p>
          </>
        )}
        <div className="quick-row">
          <button className={'btn' + (canShareFiles() ? '' : ' primary')} onClick={doExport}>
            <Icon name="download" size={20} /> Stáhnout zálohu
          </button>
          <label className="btn">
            <Icon name="upload" size={20} /> Obnovit ze zálohy
            <input type="file" accept="application/json,.json" hidden onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = ''; // ať jde stejný soubor vybrat znovu (např. po Zrušit)
                if (f) readImport(f);
              }} />
          </label>
        </div>
        {pending && (
          <Notice tone="warn">
            Obnovením se <strong>nahradí všechna data</strong> v aplikaci zálohou z {fmtDate(pending.exportedAt)}.
            <div className="quick-row" style={{ marginTop: 10 }}>
              <button className="btn danger" onClick={doImport} disabled={importing}>
                {importing ? 'Obnovuji zálohu…' : 'Nahradit data zálohou'}
              </button>
              <button className="btn" onClick={() => setPending(null)}>
                Zrušit
              </button>
            </div>
          </Notice>
        )}
        {persisted === false && (
          <Notice tone="warn">
            Prohlížeč může data při nedostatku místa smazat.{' '}
            <button className="btn link" onClick={requestPersist}>
              Požádat o trvalé uložení
            </button>
          </Notice>
        )}
      </Section>

      <Section title="Koš">
        <p className="muted">Smazaná stanoviště, včelstva a zápisy – dají se obnovit.</p>
        <button className="btn wide" onClick={() => go('kos')}>
          <Icon name="trash" size={20} /> Otevřít koš
        </button>
      </Section>

      <h2 className="settings-group">O aplikaci</h2>
      <Section title="Připomínky a chyby">
        <p className="muted">Něco nefunguje nebo vám chybí? Napište autorovi aplikace – zpráva jde přímo do aplikace, bez e‑mailu.</p>
        <button className="btn wide" onClick={() => go('pripominky')}>
          <Icon name="mail" size={20} /> Napsat připomínku / nahlásit chybu
        </button>
      </Section>
      <RecommendSetting />
      <LicenceSetting />
      </div>

      <p className="muted small center">
        Úlník · verze {__APP_VERSION__}
        <br />© 2026 Úlník · Všechna práva vyhrazena
        <br />Zkušební verze zdarma, bez záruky – pravidelně zálohujte.
      </p>
      <StatsSetting />
    </Screen>
  );
}


export function ProductForm({ id }: { id?: string }) {
  const [p, setP] = useState<Partial<Product> | null>(id ? null : { builtin: false, hidden: false });
  useEffect(() => {
    if (id) read.get('products', id).then((x) => setP(x ?? null));
  }, [id]);
  if (!p) return null;
  const set = (x: Partial<Product>) => setP({ ...p, ...x });
  const save = async () => {
    if (!p.name?.trim()) return toast('Vyplňte název.');
    // lhůta se kopíruje do každého léčení – nesmysl by pak zablokoval uložení léčení
    if (p.withdrawalDays != null && !(Number.isFinite(p.withdrawalDays) && p.withdrawalDays >= 0 && p.withdrawalDays <= 3650))
      return toast('Ochranná lhůta musí být 0 až 3650 dní.');
    await saveProduct({ ...(p as Product), name: p.name.trim() });
    saved();
    go('nastaveni', true);
  };
  return (
    <Screen title={id ? p.name ?? 'Přípravek' : 'Nový přípravek'} backTo="nastaveni">
      <Field label="Název" htmlFor="pr-name">
        <TextInput id="pr-name" value={p.name} onChange={(v) => set({ name: v })} />
      </Field>
      <Field label="Účinná látka" htmlFor="pr-sub">
        <TextInput id="pr-sub" value={p.activeSubstance} onChange={(v) => set({ activeSubstance: v })} />
      </Field>
      <Field label="Způsob aplikace">
        <Chips
          options={Object.entries(TREATMENT_METHOD) as [TreatmentMethod, string][]}
          value={p.method}
          allowNone
          onChange={(v) => set({ method: v })}
        />
      </Field>
      <div className="row two">
        <Field label="Jednotka dávky" htmlFor="pr-unit">
          <TextInput id="pr-unit" value={p.unit} onChange={(v) => set({ unit: v })} placeholder="ml, g, pásek…" />
        </Field>
        <Field label="Ochranná lhůta (dní)">
          <Stepper ariaLabel="Ochranná lhůta" value={p.withdrawalDays} max={365} unit="dní" onChange={(v) => set({ withdrawalDays: v })} />
        </Field>
      </div>
      <Notice>Dávkování a ochrannou lhůtu přebírejte z příbalového letáku a pokynů KVS pro danou sezónu.</Notice>
      <Field label="Poznámka" htmlFor="pr-note">
        <TextInput id="pr-note" multiline value={p.note} onChange={(v) => set({ note: v })} />
      </Field>
      {id && (
        <label className="check">
          <input type="checkbox" checked={!!p.hidden} onChange={(e) => set({ hidden: e.target.checked })} />
          Nepoužívám – skrýt v nabídce
        </label>
      )}
      <div className="savebar">
        <button className="btn primary big" onClick={save}>
          Uložit
        </button>
      </div>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// QR štítky k vytištění
// ---------------------------------------------------------------------------

export function LabelsScreen({ apiaryId }: { apiaryId: string }) {
  const data = useQuery(async () => ({
    apiary: await read.get('apiaries', apiaryId),
    colonies: (await read.byIndex('colonies', 'apiaryId', apiaryId)).filter((c) => c.status === 'active').sort(naturalLabel),
  }), [apiaryId]);
  if (!data?.apiary) return null;
  const base = location.href.split('#')[0];
  return (
    <Screen title="QR štítky" subtitle={data.apiary.name} backTo={`stanoviste/${apiaryId}`}>
      <p className="muted no-print">
        Vytiskněte, zalaminujte nebo přelepte průhlednou páskou a připevněte na úly. Načtením fotoaparátem telefonu se otevře karta včelstva.
      </p>
      <button className="btn primary no-print" onClick={() => window.print()}>
        <Icon name="print" size={20} /> Tisk
      </button>
      <div className="labels">
        {data.colonies.map((c) => (
          <figure key={c.id} className="qr-label">
            <div className="qr" dangerouslySetInnerHTML={{ __html: qrSvg(`${base}#/v/${c.id}`) }} />
            <figcaption>
              <strong>{colonyName(c.label)}</strong>
              <span>{data.apiary!.name}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Screen>
  );
}

function SeasonSetting() {
  const start = useQuery(async () => normStartMonth((await getSetting<number>(SEASON_SETTING)) ?? 1), []);
  if (start == null) return null;
  return (
    <Section title="Včelařský rok">
      <p className="muted">Podle čeho se počítají údaje „letos“ na kartách včelstev a roční přehledy. Volbu můžete kdykoli změnit, přehledy se přepočítají.</p>
      <Field label="Rok začíná" htmlFor="season-start">
        <select
          id="season-start"
          value={start}
          onChange={async (e) => {
            await setSetting(SEASON_SETTING, Number(e.target.value));
            confirmSmall();
          }}
        >
          {MONTHS.map((m, i) => (
            <option key={i} value={i + 1}>
              {i === 0 ? 'Kalendářní rok (leden–prosinec)' : `Od 1. ${i + 1}. – včelařský rok (${m}–${MONTHS[i - 1]})`}
            </option>
          ))}
        </select>
      </Field>
      <p className="small muted">
        Nyní: {seasonSettingLabel(start)}, aktuální rok je <strong>{seasonOf(todayISO(), start).label}</strong>. Evidence léčiv se vede vždy po
        kalendářních letech.
      </p>
    </Section>
  );
}
